"use client";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Avatar, AvatarFallback, Button, Card, CardContent, Collapsible, CollapsibleContent, CollapsibleTrigger } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import { Learning, NavArrowDown, Rocket, SearchEngine } from "iconoir-react";
import Link from "next/link";

import { SITE_CONFIG } from "@src/config/site.config";
import { useServices } from "@src/context/ServicesProvider";

export const WelcomePanel: React.FC = () => {
  const { urlService } = useServices();
  const [expanded, setExpanded] = useState(true);

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-3xl font-semibold">
          <FormattedMessage id="home.welcomepanel.title" defaultMessage="Welcome to {productName}!" values={{ productName: SITE_CONFIG.productName }} />
        </h2>
        <CollapsibleTrigger asChild>
          <Button size="icon" variant="ghost" className="!m-0 rounded-full" onClick={() => setExpanded(prev => !prev)}>
            <NavArrowDown fontSize="1rem" className={cn("transition-all duration-100", { ["rotate-180"]: expanded })} />
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <Card>
          <CardContent className="p-6">
            <ul className="space-y-6">
              <li className="flex items-center space-x-4">
                <Avatar className="h-12 w-12 rounded-md">
                  <AvatarFallback className="rounded-md">
                    <Rocket className="rotate-45" />
                  </AvatarFallback>
                </Avatar>

                <div className="flex flex-col">
                  <Link href={urlService.getStarted()} className="font-semibold">
                    <FormattedMessage
                      id="home.welcomepanel.getting_started"
                      defaultMessage="Getting started with {productName}"
                      values={{ productName: SITE_CONFIG.productName }}
                    />
                  </Link>
                  <span className="text-sm text-muted-foreground">
                    <FormattedMessage
                      id="home.welcomepanel.getting_started_desc"
                      defaultMessage="Learn how to deploy your first docker container on {brandShortName} in a few clicks using Console."
                      values={{ brandShortName: SITE_CONFIG.brandShortName }}
                    />
                  </span>
                </div>
              </li>

              <li className="flex items-center">
                <Avatar className="h-12 w-12 rounded-md">
                  <AvatarFallback className="rounded-md">
                    <SearchEngine />
                  </AvatarFallback>
                </Avatar>

                <div className="ml-4 flex flex-col">
                  <Link href={urlService.templates()} className="font-semibold">
                    <FormattedMessage id="home.welcomepanel.explore_marketplace" defaultMessage="Explore the marketplace" />
                  </Link>
                  <span className="text-sm text-muted-foreground">
                    <FormattedMessage
                      id="home.welcomepanel.explore_marketplace_desc"
                      defaultMessage="Browse through the marketplace of pre-made solutions with categories like blogs, blockchain nodes and more!"
                    />
                  </span>
                </div>
              </li>

              <li className="flex items-center">
                <Avatar className="h-12 w-12 rounded-md">
                  <AvatarFallback className="rounded-md">
                    <Learning />
                  </AvatarFallback>
                </Avatar>

                <div className="ml-4 flex flex-col">
                  <Link href={SITE_CONFIG.domains.docs} target="_blank" className="font-semibold">
                    <FormattedMessage
                      id="home.welcomepanel.learn_more"
                      defaultMessage="Learn more about {brandShortName}"
                      values={{ brandShortName: SITE_CONFIG.brandShortName }}
                    />
                  </Link>
                  <span className="text-sm text-muted-foreground">
                    <FormattedMessage
                      id="home.welcomepanel.learn_more_desc"
                      defaultMessage="Want to know about the advantages of using a decentralized cloud compute marketplace?"
                    />
                  </span>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
};
